// Ported from authentication/loading-button/loading-button.tpl.html + .component.ng1.ts.
//
// Class names are verbatim (`loading-button`, `text`, `loading-button-loader`) and so is the
// structure, because _loading-button.scss came across unchanged and the visual-diff gate
// compares against production pixels.
//
// ⚠️ THE ELEMENT HOST IS LOAD-BEARING HERE TOO, and reading `_loading-button.scss` alone
// hides it: that file only styles the `.loading-button` CLASS. The sizing lives in the PAGE
// stylesheets, scoped to the ELEMENT — `_login.page.scss:71` and `_sign-up.page.scss:80`
// both open `loading-button { width: 100%; .form-btn { @include popup-btn-form(); width:
// 100%; } }`. Render only the `<button>`, as the first port did, and the submit button on
// both screens loses its width and the whole popup-button mixin.
import { createElement, useState, type ReactElement } from 'react'
import { useAutofocusAttribute } from './autofocusAttribute.ts'

export interface LoadingButtonProps {
  text: string
  /** Upstream's `ng-class-override`: the caller supplies the layout class, e.g. `form-btn`. */
  classOverride?: string
  tabIndex?: number
  /** `ng-attr-autofocus` — the ATTRIBUTE, which is inert after parse. See `useAutofocusAttribute`. */
  autoFocus?: boolean
  disabled?: boolean
  /**
   * Upstream shows the spinner while the promise the click handler returns is pending, which
   * is what makes this a "loading" button rather than a styled one.
   */
  onClick: () => void | Promise<unknown>
}

export function LoadingButton({
  text,
  classOverride,
  tabIndex,
  autoFocus,
  disabled = false,
  onClick,
}: LoadingButtonProps): ReactElement {
  const [loading, setLoading] = useState(false)
  const autofocusRef = useAutofocusAttribute()

  const handleClick = (): void => {
    const result = onClick()
    if (!(result instanceof Promise)) return
    setLoading(true)
    // `then` with BOTH branches, not `finally`: the promise `finally` returns stays rejected
    // when `onClick` rejects, and discarding it produces an unhandled rejection. Every caller
    // here handles its own failure in the handler, so this only has to stop the spinner.
    result.then(
      () => {
        setLoading(false)
      },
      () => {
        setLoading(false)
      },
    )
  }

  return createElement(
    'loading-button',
    null,
    <button
      type="button"
      className={['loading-button', classOverride].filter(Boolean).join(' ')}
      onClick={handleClick}
      disabled={loading || disabled}
      tabIndex={tabIndex}
      // ⚠️ THE ATTRIBUTE, NOT REACT'S PROP. Upstream writes `ng-attr-autofocus`, which does
      // nothing on a template inserted after parse; React's prop calls `.focus()` and Chrome
      // then draws a focus ring production does not have — see `useAutofocusAttribute`.
      ref={autoFocus === true ? autofocusRef : undefined}
    >
      {loading ? (
        // FontAwesome classes, as upstream. The icon font itself arrives with the rest of
        // the vendor cascade; until then this renders as an empty <i>, which is visible in
        // a diff rather than silently wrong.
        <i className="far fa-spinner fa-spin loading-button-loader" />
      ) : (
        <span className="text">{text}</span>
      )}
    </button>,
  )
}
